import { useCallback, useEffect, useRef, useState } from "react";
import {
  enrichQuotesWithSparklineChange,
  filterQuotesCarouselItems,
  filterStableMarketQuotes,
  findMarketQuoteForAsset,
  getMarketSparkline,
  getStablesApiErrorMessage,
  mapMarketSparklinesByAsset,
  orderQuotesCarouselItems,
  resolveMarketQuoteUnitPrice,
  supplementMissingMarketQuotes,
} from "../Services/stablesApi";
import { isPositiveDecimalString } from "../Services/stablesMoney";
import {
  getCachedMarketQuotes,
  invalidateMarketQuotesCache,
} from "../Services/marketQuotesCache";
import { formatBrlFromString } from "../Services/stablesMoney";
import {
  DISPLAY_MARKET_QUOTE_AMOUNT,
  STABLE_QUOTES_CAROUSEL_ASSET_IDS,
} from "../Pages/Private/Stables/constants";

export { resolveMarketQuoteUnitPrice as resolveStableMarketQuotePrice };

export const formatMarketQuotePrice = (price, quoteCurrency = "BRL") => {
  const formatted = formatBrlFromString(price, 4);
  return String(quoteCurrency || "BRL").trim().toUpperCase() === "BRL"
    ? `R$ ${formatted}`
    : formatted;
};

export const formatMarketQuoteChange = (changePercent, positive) => {
  const value = Number(changePercent) || 0;
  const sign = positive !== false && value >= 0 ? "+" : "";
  return `${sign}${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;
};

const resolvePerAssetQuote = async (assetId, { force = false } = {}) => {
  const assetKey = String(assetId || "").trim().toLowerCase();
  if (!assetKey) return null;

  try {
    const result = await getCachedMarketQuotes(
      {
        side: "buy",
        mode: "amount",
        amount: DISPLAY_MARKET_QUOTE_AMOUNT,
        asset: assetKey,
      },
      { force }
    );
    const selected = findMarketQuoteForAsset(result, assetKey);
    return selected ? { ...selected, price: resolveMarketQuoteUnitPrice(selected) } : null;
  } catch {
    return null;
  }
};

const enrichWithSparklineChange = async (items, { assetId } = {}) => {
  if (!items.length) return items;

  try {
    const assetIds = assetId
      ? [String(assetId).trim().toLowerCase()]
      : items.map((item) => item.asset ?? item.id).filter(Boolean);
    if (!assetIds.length) return items;

    const sparklinePayload = await getMarketSparkline({ assets: assetIds.join(","), window: "hour" });
    const byAsset = mapMarketSparklinesByAsset(sparklinePayload);
    return enrichQuotesWithSparklineChange(items, byAsset);
  } catch {
    return items;
  }
};

const priceQuoteItems = (items = []) =>
  items
    .map((item) => ({
      ...item,
      price: resolveMarketQuoteUnitPrice(item),
    }))
    .filter((item) => isPositiveDecimalString(item.price));

export const useStablesMarketQuotes = ({
  includeSparklineChange = true,
  assetId = null,
  carouselOnly = false,
  /** Resolve a single asset from the same carousel quotes pipeline (shared cache as StablesQuotesCarousel). */
  matchCarousel = false,
  /** Auto-refresh interval in ms; only applies when carouselOnly is true. */
  autoRefreshMs = 0,
  /** When false, skips fetch (e.g. quote supplied by parent). */
  enabled = true,
} = {}) => {
  const useCarouselPipeline = carouselOnly || matchCarousel;
  const shouldLoadSparkline = includeSparklineChange;
  const shouldAutoRefresh = carouselOnly && autoRefreshMs > 0;
  const [quotes, setQuotes] = useState([]);
  const [requestAmount, setRequestAmount] = useState(DISPLAY_MARKET_QUOTE_AMOUNT);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const loadGenerationRef = useRef(0);
  const inFlightRef = useRef(false);

  const load = useCallback(async ({ force = false, silent = false } = {}) => {
    if (!enabled || inFlightRef.current) return;

    inFlightRef.current = true;
    const generation = ++loadGenerationRef.current;

    if (!silent) {
      setLoading(true);
      setRefreshing(false);
    } else {
      setRefreshing(true);
    }
    setError(null);

    try {
      let pricedItems = [];
      let amount = DISPLAY_MARKET_QUOTE_AMOUNT;

      if (assetId) {
        if (useCarouselPipeline) {
          const result = await getCachedMarketQuotes(
            {
              side: "buy",
              mode: "amount",
              amount: DISPLAY_MARKET_QUOTE_AMOUNT,
            },
            { force }
          );
          if (generation !== loadGenerationRef.current) return;

          amount = Number(result.amount) || DISPLAY_MARKET_QUOTE_AMOUNT;
          let carouselItems = priceQuoteItems(result.items ?? []);
          carouselItems = orderQuotesCarouselItems(filterQuotesCarouselItems(carouselItems));
          carouselItems = await supplementMissingMarketQuotes(
            carouselItems,
            STABLE_QUOTES_CAROUSEL_ASSET_IDS
          );
          carouselItems = priceQuoteItems(carouselItems);
          carouselItems = orderQuotesCarouselItems(filterQuotesCarouselItems(carouselItems));

          const selected = findMarketQuoteForAsset(
            carouselItems.length ? { items: carouselItems, ...result } : result,
            assetId
          );
          pricedItems = selected
            ? [{ ...selected, price: resolveMarketQuoteUnitPrice(selected) }]
            : [];
        } else {
          const perAssetQuote = await resolvePerAssetQuote(assetId, { force });
          if (generation !== loadGenerationRef.current) return;

          if (perAssetQuote) {
            pricedItems = [perAssetQuote];
            amount = DISPLAY_MARKET_QUOTE_AMOUNT;
          }
        }
      } else {
        const result = await getCachedMarketQuotes(
          {
            side: "buy",
            mode: "amount",
            amount: DISPLAY_MARKET_QUOTE_AMOUNT,
          },
          { force }
        );
        if (generation !== loadGenerationRef.current) return;

        amount = Number(result.amount) || DISPLAY_MARKET_QUOTE_AMOUNT;
        pricedItems = priceQuoteItems(result.items ?? []);

        if (useCarouselPipeline) {
          const carouselItems = orderQuotesCarouselItems(filterQuotesCarouselItems(pricedItems));
          pricedItems = await supplementMissingMarketQuotes(
            carouselItems,
            STABLE_QUOTES_CAROUSEL_ASSET_IDS
          );
          pricedItems = priceQuoteItems(pricedItems);
          pricedItems = orderQuotesCarouselItems(filterQuotesCarouselItems(pricedItems));
        } else {
          pricedItems = filterStableMarketQuotes(pricedItems);
        }

        if (!pricedItems.length) {
          throw new Error("Empty quotes");
        }
      }

      if (assetId && !pricedItems.length) {
        setQuotes([]);
        setRequestAmount(amount);
        setError(null);
        if (!silent) {
          setLoading(false);
        }
        return;
      }

      setQuotes(pricedItems);
      setRequestAmount(amount);
      if (!silent) {
        setLoading(false);
      }

      if (!shouldLoadSparkline) return;

      const enrichedItems = priceQuoteItems(
        await enrichWithSparklineChange(pricedItems, { assetId })
      );
      if (generation !== loadGenerationRef.current) return;
      setQuotes(enrichedItems);
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      if (!silent) {
        setQuotes([]);
        setRequestAmount(DISPLAY_MARKET_QUOTE_AMOUNT);
        setError(getStablesApiErrorMessage(err, "Não foi possível carregar a cotação."));
        setLoading(false);
      }
    } finally {
      if (generation === loadGenerationRef.current) {
        inFlightRef.current = false;
        if (silent) {
          setRefreshing(false);
        }
      }
    }
  }, [shouldLoadSparkline, assetId, useCarouselPipeline, enabled]);

  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve();
    inFlightRef.current = false;
    invalidateMarketQuotesCache();
    const keepVisible = carouselOnly && quotes.length > 0;
    return load({ force: true, silent: keepVisible });
  }, [load, carouselOnly, quotes.length, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    load();
  }, [load, enabled]);

  useEffect(() => {
    if (!shouldAutoRefresh) return undefined;

    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      invalidateMarketQuotesCache();
      load({ force: true, silent: true });
    };

    const interval = setInterval(refresh, autoRefreshMs);
    return () => clearInterval(interval);
  }, [shouldAutoRefresh, autoRefreshMs, load]);

  return { quotes, requestAmount, loading, refreshing, error, reload };
};
